import { useCallback, useEffect, useMemo, useState } from 'react';
import { del, fmtDate, put, type ApiErr } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { Banner, Block, Cell, Field, Grid, Kpis, Log, Pill, Seg, Strip, Table, Toggle, type Row, type Tone } from '../ui/Block';
import { Head, useAdmin } from '../ui/Shell';
import { confirmSimple } from './dataShared';
import { kindOf, toneOf } from './Simulator';
import { STRATEGIES, STRATEGY_SUB, type Action, type BalancerConfig, type Sample, type SimConfig } from './simulator/engine';
import { Flow, FlowLegend, type FleetSnap } from './simulator/Flow';
import { LineChart } from './simulator/LineChart';
import { GpuGroups } from './simulator/GpuGroups';
import { Fields, POOL, RULES, SPEECH, SpeechBrake } from './simulator/Settings';

type Mode = 'off' | 'watch' | 'live';

export function plainWhy(why: string | null | undefined): string | null {
  if (!why) return null;
  const w = why.toLowerCase();
  const pod = why.match(/pod (\d+) \(/)?.[1];
  if (w.includes('cheaper for its')) return `Trying a cheaper GPU to move pod ${pod ?? '?'}'s session to (saves money, not needed for capacity)`;
  if (w.includes('runpod has no')) return 'RunPod had no such GPU free: never created, not billed';
  if (w.includes('no container after')) return 'RunPod never started it (broken host): given up';
  if (w.includes('live session(s) without a gpu slot')) return 'Started for a live session that had no GPU slot';
  if (w.includes('lost the race')) return 'Lost the race: another speech machine was ready first';
  if (w.includes('ready in the same moment')) return 'Ready at the same moment as another speech machine: one kept';
  if (w.includes('a slow host')) return 'Not ready by the boot cap (a slow host): the next offer raced in its place';
  if (w.includes('starts speech machine')) return 'Raced on Vast for voices only: RunPod had no GPU';
  if (w.includes('vast had no machine')) return 'Vast had no machine at or under the price cap';
  if (w.includes('runpod pod is up for the voices')) return 'A RunPod pod came up for the voices first';
  if (w.includes('a voice is wanted')) return 'Started because someone asked for a voice';
  if (w.includes('a reading has')) return 'Started because a long spoken reading had every speech slot busy';
  if (w.includes('always on')) return 'Kept on by the minimum-pods setting';
  if (w.includes('no live session needs it')) return 'No longer needed: the session it was for found a slot';
  if (w.includes('which it was to replace')) return 'The pod it was to replace has no live session left';
  if (w.includes(' empty')) return 'No sessions left on it: shut down';
  if (w.includes('is ready for its')) return 'Its sessions moved to a cheaper pod';
  if (w.includes('without an owner')) return 'Found on RunPod with no record here: shut down';
  if (w.includes('heartbeat')) return 'Stopped answering (host lost)';
  return why.length > 120 ? `${why.slice(0, 117)}…` : why;
}
interface RunPodSettings { image: string; cloudType: string; containerDiskGb: number; minVcpuPerGpu: number; firstPort: number; lowBalanceUsd: number; testSpendCapPerDay: number }
interface SpeechLimits { accountAsksPerMin: number }
interface VastSettings { enabled: boolean; image: string; diskGb: number; minGpuRamGb: number; minReliability: number; minCuda: number; minInetDownMbps: number; minCpuCores: number; minRamGb: number; bootGb: number; bootWeight?: number; badHostH?: number; excludeCountries: string[] }
interface PoolSettings { mode: Mode; balancer: BalancerConfig; runpod: RunPodSettings; speech?: SpeechLimits; vast?: VastSettings }
interface PodRow {
  id: number; providerId: string | null; gpu: string; slots: number; perH: number; costPerH: number | null; state: string; watch: boolean;
  slotsUp: number; slotsTaken: number; createdAt: string; containerAt: string | null; readyAt: string | null; heartbeatAt: string | null; endedAt: string | null; endReason: string | null;
  tts?: TtsCapacity | null;
  provider?: 'runpod' | 'vast'; speechOnly?: boolean;
  gpuName?: string | null; gpuTypeId?: string | null; dataCenter?: string | null; sessions?: number; rttMs?: number | null;
  asked?: { gpus?: string[]; dataCenters?: string[] } | null;
}
interface TtsCapacity {
  gpu?: boolean; full?: boolean; reason?: string | null; budget_mb?: number; resident_mb?: number; pool_mb?: number; pool_used_mb?: number;
  room_mb?: number; device_total_mb?: number | null; device_used_mb?: number | null; rtf_p90?: number; compute_full?: boolean;
  wait_p90_s?: number; full_wait_s?: number;
  lanes?: number; queued?: number; waiting?: number; lines_last_min?: number;
  profile?: { lanes: number; lines: number; lines_per_min: number; line_s_p50: number; audio_s_per_s: number; took_s: number } | null;
  speech?: { adaptive?: boolean; target?: number; loaded?: number; busy?: number; floor?: number; most?: number; engines?: number; enginesBusy?: number; reserveMb?: number; want?: string | null };
  models?: { key: string; resident_mb: number }[]; voices?: { voice: string; last_used_s: number }[];
}

const gb = (mb: number) => `${(mb / 1024).toFixed(1)} GB`;
const providerName = (p: Pick<PodRow, 'provider'>) => (p.provider === 'vast' ? 'Vast' : 'RunPod');

function Meter({ label, segs, value, sub }: { label: string; segs: { frac: number; bg: string; title: string }[]; value: string; sub?: string }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
        <span style={{ color: 'var(--ink-80)' }}>{label}</span>
        <span className="mono" style={{ color: 'var(--ink-62)', whiteSpace: 'nowrap' }}>{value}</span>
      </div>
      <div className="hbar__track" style={{ display: 'flex' }}>
        {segs.map((g) => (
          <div key={g.title} title={g.title} style={{ height: '100%', width: `${Math.max(0, Math.min(1, g.frac)) * 100}%`, background: g.bg }} />
        ))}
      </div>
      {sub && <span style={{ fontSize: 11, color: 'var(--ink-55)' }}>{sub}</span>}
    </div>
  );
}

function PodCapacity({ p }: { p: PodRow }) {
  const t = p.tts;
  const slotsFull = p.slotsUp > 0 && p.slotsTaken >= p.slotsUp;
  const full = slotsFull || !!t?.full;
  const why = t?.full ? `TTS full (${t.reason ?? '?'})` : slotsFull ? 'every slot taken' : null;
  const budget = t?.budget_mb ?? 0;
  const resident = t?.resident_mb ?? 0;
  const pool = t?.pool_mb ?? 0;
  const poolUsed = t?.pool_used_mb ?? 0;
  const wait = t?.wait_p90_s ?? 0;
  const fullWait = t?.full_wait_s ?? 2;
  return (
    <div style={{ display: 'grid', gap: 12, padding: 14, border: '1px solid var(--fill-06)', borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span className="mono" style={{ fontSize: 13 }}>pod {p.id} <span style={{ color: 'var(--ink-55)' }}>· {providerName(p)} · {p.gpuName ?? p.gpu} · {money(p.costPerH ?? p.perH)}/h</span></span>
        {p.speechOnly ? <Pill tone="info">speech only</Pill> : <Pill tone={full ? 'bad' : 'ok'}>{full ? 'no new sessions' : 'taking sessions'}</Pill>}
      </div>
      {why && <span style={{ fontSize: 12, color: 'var(--conf-low)' }}>{why}</span>}
      {p.speechOnly && t && (
        <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>
          {t.profile ? `measured at boot: ${Math.round(t.profile.lines_per_min)} lines a minute on ${t.profile.lanes} lanes, ${t.profile.line_s_p50.toFixed(2)} s a line` : 'measuring itself after boot…'}
          {` · now ${t.lines_last_min ?? 0} lines a minute${t.waiting || t.queued ? `, ${(t.waiting ?? 0) + (t.queued ?? 0)} waiting` : ''}`}
        </span>
      )}
      {p.speechOnly ? (
        <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>Transcriptions: none, ever (a speech-only machine runs only the voice server)</span>
      ) : (
        <Meter label="Transcriptions" value={`${p.slotsTaken} of ${p.slots}`} sub={p.slotsUp < p.slots ? `${p.slotsUp} slots up` : undefined}
          segs={[{ frac: p.slots ? p.slotsTaken / p.slots : 0, bg: slotsFull ? 'var(--conf-low)' : 'var(--brand)', title: 'sessions' }]} />
      )}
      {t && <SpeechMeter t={t} />}
      {!t ? (
        <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>TTS: no answer yet</span>
      ) : !t.gpu ? (
        <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>TTS on the CPU: no GPU budget</span>
      ) : (
        <>
          <Meter label="TTS memory" value={budget ? `${gb(resident + poolUsed)} of ${gb(budget)}` : 'no budget'}
            sub={`${(t.models ?? []).length} models loaded · ${(t.voices ?? []).length} voices speaking · ${gb(t.room_mb ?? 0)} free for new voices`}
            segs={budget ? [
              { frac: resident / budget, bg: t.reason === 'memory' ? 'var(--conf-low)' : 'var(--brand)', title: `voices: ${gb(resident)}` },
              { frac: poolUsed / budget, bg: 'var(--conf-mid)', title: `batches running: ${gb(poolUsed)}` },
              { frac: Math.max(0, pool - poolUsed) / budget, bg: 'var(--fill-16)', title: `batch pool held back: ${gb(Math.max(0, pool - poolUsed))}` },
            ] : []} />
          <Meter label="TTS queue" value={`${wait.toFixed(1)} s wait`} sub={`p90 wait for the GPU; no new voices past ${fullWait} s · clips take ${(t.rtf_p90 ?? 0).toFixed(2)}× their length`}
            segs={[{ frac: wait / fullWait, bg: t.compute_full ? 'var(--conf-low)' : wait > fullWait * 0.7 ? 'var(--conf-mid)' : 'var(--ok)', title: 'p90 of the wait between a clip arriving and its batch starting' }]} />
        </>
      )}
      {t?.device_total_mb ? (
        <Meter label="GPU memory (all processes)" value={`${gb(t.device_used_mb ?? 0)} of ${gb(t.device_total_mb)}`}
          segs={[{ frac: (t.device_used_mb ?? 0) / t.device_total_mb, bg: 'var(--ink-62)', title: 'used on the device' }]} />
      ) : null}
    </div>
  );
}

function speechOf(t: TtsCapacity): { busy: number; lanes: number; most: number; queued: number } {
  const lanes = t.speech?.target ?? t.lanes ?? 0;
  return { busy: t.speech?.busy ?? 0, lanes, most: Math.max(lanes, t.speech?.most ?? lanes), queued: t.queued ?? 0 };
}

function SpeechMeter({ t }: { t: TtsCapacity }) {
  const s = speechOf(t);
  const sp = t.speech;
  const sub = [
    s.queued ? `${s.queued} lines queued` : 'no lines queued',
    sp?.adaptive ? `${sp.floor ?? '?'} always, up to ${s.most} while transcription slots are free` : 'fixed at boot',
    sp?.adaptive && sp.reserveMb ? `${gb(sp.reserveMb)} kept for live sessions` : null,
  ].filter(Boolean).join(' · ');
  return (
    <Meter label="Speech" value={`${s.busy} of ${s.lanes} slots`} sub={sub}
      segs={[
        { frac: s.most ? s.busy / s.most : 0, bg: 'var(--conf-mid)', title: `speaking: ${s.busy}` },
        { frac: s.most ? Math.max(0, s.lanes - s.busy) / s.most : 0, bg: 'var(--fill-16)', title: `open: ${Math.max(0, s.lanes - s.busy)}` },
      ]} />
  );
}

function ttsCell(t: PodRow['tts']): { t: string; sub?: string; fg?: string } {
  if (!t) return { t: '—', sub: 'no answer yet' };
  const used = t.resident_mb ?? 0;
  const budget = t.budget_mb ?? 0;
  const pct = budget ? Math.round((100 * used) / budget) : 0;
  return {
    t: t.full ? `full${t.reason ? ` (${t.reason})` : ''}` : `${pct}%`,
    sub: budget ? `${(used / 1024).toFixed(1)} of ${(budget / 1024).toFixed(1)} GB · ${(t.voices ?? []).length} voices` : undefined,
    fg: t.full ? 'var(--conf-low)' : undefined,
  };
}
interface DecisionRow { at: string; mode: string; action: Action; why: string; outcome: string | null }
interface Demand { overflow: number; breakEven: number; startAt: number; drainAt: number; spare: number; movable: number; highFor: number; startIn: number | null; blocked: string | null; pressure: string | null; speech?: boolean; speechCapped?: string | null }
interface StatusReply {
  settings: PoolSettings; savedAt: string | null; spendToday: number; runpodConfigured: boolean; alerts: string[];
  vastConfigured?: boolean; speechMachines?: boolean;
  runpodKey: { source: 'saved' | 'env'; last4: string; setAt: string | null } | null;
  fleet: FleetSnap | null;
  actions24h?: Record<string, number>;
  runpodAccount: { balance: number; spendPerH: number; hoursLeft: number | null; low: boolean } | { error: string } | null;
  balancer: {
    demand?: Demand;
    sessions?: Record<string, { place: { at: string; pod?: number } }>;
    pods?: Record<string, { id: number; state: string; createdAt: number; gpu: { name: string; perH: number; slots: number } }>;
  } | null;
  pods: PodRow[]; decisions: DecisionRow[];
  launches?: LaunchEvent[];
}
interface LaunchEvent { at: string; pod: number | null; kind: string; detail: Record<string, unknown> }

const POD_ROWS = 20;

type Snap = Record<string, any>;

function inS(until: number | undefined, now: number): string | null {
  if (until == null || !(until > now)) return null;
  const s = Math.round(until - now);
  return s >= 90 ? `${Math.round(s / 60)} min` : `${s} s`;
}

function WhyNoGpu({ bal, blocked, onFly, mobile }: { bal: Snap; blocked: string | null; onFly: number; mobile: boolean }) {
  const now: number = bal.now ?? Date.now() / 1000;
  const cfg: Snap = bal.cfg ?? {};
  const capPerH: number = cfg.maxFirstPerH ?? Infinity;
  const stock: Record<string, [string, number]> = bal.stock ?? {};
  const bad: Record<string, number> = bal.gpuBadUntil ?? {};
  const refused: Record<string, number> = bal.refusedUntil ?? {};
  const names = new Set<string>();
  for (const t of cfg.gpuTypes ?? []) names.add(t.name);
  for (const k of Object.keys(stock)) names.add(k.split('@')[0]);
  const price = (n: string): number | null => {
    const g = (cfg.gpuTypes ?? []).find((x: Snap) => x.name === n);
    return g ? g.perH : null;
  };
  const rows: Row[] = [...names]
    .map((n) => ({ n, p: price(n) }))
    .filter((x) => x.p != null)
    .sort((a, b) => (a.p as number) - (b.p as number))
    .map(({ n, p }) => {
      const overall = stock[`${n}@`]?.[0] ?? 'unknown';
      const dcs = Object.entries(stock).filter(([k, v]) => k.startsWith(`${n}@`) && k !== `${n}@` && v[0] !== 'none').map(([k, v]) => `${k.split('@')[1]} ${v[0]}`);
      const under = (p as number) <= capPerH + 1e-9;
      const out = inS(bad[n], now);
      const usable = under && overall !== 'none' && !out;
      return {
        key: n,
        cells: [
          { t: n, mono: true },
          { t: `$${(p as number).toFixed(2)}/h`, sub: under ? 'under the price cap' : `over the $${capPerH.toFixed(2)}/h cap`, fg: under ? undefined : 'var(--conf-mid)' },
          { pill: { label: overall, tone: (overall === 'none' ? 'bad' : overall === 'low' ? 'warn' : overall === 'unknown' ? 'mute' : 'ok') as Tone }, sub: dcs.length ? dcs.join(' · ') : undefined },
          { t: out ? `left out ${out} more` : '—', fg: out ? 'var(--conf-low)' : undefined },
          { t: usable ? 'can be asked' : !under ? 'too dear' : overall === 'none' ? 'RunPod has none' : out ? 'failed lately' : '—', fg: usable ? 'var(--ok)' : 'var(--conf-low)' },
        ],
      };
    });
  const waits = Object.entries(refused).map(([k, t]) => ({ k, left: inS(t, now) })).filter((x) => x.left);
  const head = blocked ?? (onFly ? `${onFly} live session(s) on Fly and no GPU pod up or coming` : 'no GPU pod up or coming');
  return (
    <Block title="Why no GPU" sub="What the balancer would buy and what stops it, from its last run." bg="var(--conf-mid-soft)">
      <div style={{ padding: '12px 14px', fontSize: 14 }}><b>{head}</b>{waits.length > 0 && <div style={{ marginTop: 6, opacity: 0.85 }}>RunPod refused and is asked again in: {waits.map((w) => `${w.k} ${w.left}`).join(' · ')}</div>}</div>
      <Table cols={[{ label: 'GPU', width: 'minmax(140px, 1fr)' }, { label: 'Price', width: '170px' }, { label: 'RunPod stock', width: 'minmax(180px, 1.2fr)' }, { label: 'Bad', width: '150px' }, { label: 'Verdict', width: '150px' }]} rows={rows} minWidth={800} mobile={mobile} />
    </Block>
  );
}

const noStock = (why: string | null | undefined) => {
  const w = (why ?? '').toLowerCase();
  return w.startsWith('runpod had no stock') || w.includes('no longer any instances') || w.includes('no instances available') || w.includes('not enough free gpus') || w.includes('no gpu available');
};
const stockNote = (dcs: unknown) => `RunPod had no stock in ${Array.isArray(dcs) && dcs.length ? dcs.join(', ') : 'any data centre'}, trying further`;

function launchCells(e: LaunchEvent): { what: string; detail: string; sub?: string; fg?: string } {
  const d = e.detail as Record<string, any>;
  switch (e.kind) {
    case 'created':
      if (d.provider === 'vast') return { what: 'Vast', detail: `instance ${d.instance ?? '?'} · ${d.given ?? '?'}${d.dataCenter ? ` in ${d.dataCenter}` : ''} · speech only`, sub: `offer ${d.offer ?? '?'}${d.machine ? ` · machine ${d.machine}` : ''} · $${Number(d.costPerH ?? 0).toFixed(2)}/h all in · reliability ${Number(d.reliability ?? 0).toFixed(3)}${d.verified ? ' · verified' : ''}${d.estBootS != null ? ` · boot ~${d.estBootS} s by its network and disk` : ''}`, fg: 'var(--brand)' };
      return { what: 'created', detail: `${d.provider ?? '?'} · ${d.given ? `${d.given}${d.dataCenter ? ` in ${d.dataCenter}` : ''}` : 'asked'} · any of ${(d.gpu ?? []).join(', ')}`, sub: `at least ${d.minVcpu ?? '?'} vCPU${(d.dataCenters ?? []).length ? ` · ${(d.dataCenters as string[]).join(', ')}` : ' · any data centre'}${d.costPerH != null ? ` · $${Number(d.costPerH).toFixed(2)}/h` : ''}` };
    case 'create_failed':
      if (d.provider === 'vast') return { what: d.noStock ? 'no Vast offer' : 'Vast refused', detail: String(d.error ?? '?'), fg: d.noStock ? 'var(--conf-mid)' : 'var(--conf-low)' };
      if (d.noStock || noStock(String(d.error ?? ''))) return { what: 'no stock', detail: stockNote(d.dataCenters), sub: String(d.error ?? ''), fg: 'var(--conf-mid)' };
      return { what: 'refused', detail: String(d.error ?? '?'), fg: 'var(--conf-low)' };
    case 'provider':
      if (d.error) return { what: 'RunPod', detail: `no answer: ${d.error}`, fg: 'var(--conf-mid)' };
      return {
        what: 'RunPod',
        detail: `${d.status ?? '?'} · ${d.machine ? `host ${d.machine} (${d.dataCenter ?? d.location ?? '?'})` : 'no host yet'} · container ${d.container ? `up (${d.ports ?? 0} ports)` : 'not started'}`,
        sub: [d.statusChange, d.vcpu != null ? `${d.vcpu} vCPU` : null, d.hostGpusFree != null ? `${d.hostGpusFree} GPUs free on the host` : null, d.downloadMbps ? `${d.downloadMbps} Mbps` : null].filter(Boolean).join(' · '),
      };
    case 'vast':
      return { what: 'Vast', detail: `${d.status || 'no status yet'}${d.location ? ` · ${d.location}` : ''}${d.gpu ? ` · ${d.gpu}` : ''}`, sub: String(d.message ?? '') };
    case 'boot':
      return { what: 'boot', detail: [d.startedAfterS != null ? `image + container ${d.startedAfterS} s` : null, d.curl_s != null ? `curl installed ${d.curl_s} s` : null, d.cloudflared_s != null ? `tunnel tool ${d.cloudflared_s} s` : null, d.bundle_s != null ? `bundle ${d.bundle_s} s` : null, d.tts_s != null ? `voice server ${d.tts_s} s` : null].filter(Boolean).join(' · ') || 'starting', sub: 'seconds after the start script began (image pull before it)' };
    case 'vast_host':
      return { what: d.why === 'failed' ? 'host failed' : 'slow host', detail: `machine ${d.machine ?? '?'}${d.dataCenter ? ` in ${d.dataCenter}` : ''}: left out of the next races for a few hours`, sub: [d.readyS != null ? `ready after ${d.readyS} s` : null, d.notReadyAfterS != null ? `not ready after ${d.notReadyAfterS} s` : null, d.status ? String(d.status) : null, d.message ? String(d.message) : null].filter(Boolean).join(' · '), fg: 'var(--conf-mid)' };
    case 'tunnel':
      return { what: 'tunnel', detail: `voice server reachable at ${d.url ?? '?'}`, fg: 'var(--ok)' };
    case 'warn':
      return { what: 'warning', detail: String(d.why ?? '?'), fg: 'var(--conf-mid)' };
    case 'container_up':
      return { what: 'container up', detail: d.speechOnly ? 'Vast says running' : `${d.engines ?? '?'} of ${d.slots ?? '?'} engines answer`, fg: 'var(--ok)' };
    case 'ready':
      return { what: 'ready', detail: d.speechOnly ? `voice server answering (speech only)${d.readyS != null ? ` ${d.readyS} s after its create` : ''}` : `${d.slots ?? '?'} slots`, fg: 'var(--ok)' };
    case 'ended':
      return { what: 'failed', detail: String(d.cause ?? '?'), sub: String(d.why ?? ''), fg: 'var(--conf-low)' };
    default:
      return { what: e.kind, detail: JSON.stringify(d) };
  }
}

const MODES: { value: Mode; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'watch', label: 'Watch' },
  { value: 'live', label: 'Live' },
];
const MODE_SUB: Record<Mode, string> = {
  off: 'No pod starts. Pods still open drain and stop.',
  watch: 'The balancer decides on real traffic against a stand-in for RunPod. Nothing is created or moved: its log shows what it would have done.',
  live: 'Pods are created and stopped on RunPod, and sessions connect to their free slots.',
};
const STATE_TONE: Record<string, Tone> = { creating: 'info', booting: 'info', ready: 'ok', draining: 'warn', stopped: 'mute', gone: 'mute' };

const RANGES = [{ value: '6', label: '6 h' }, { value: '24', label: '24 h' }, { value: '168', label: '7 d' }] as const;
type Range = (typeof RANGES)[number]['value'];
const fmtClock = (t: number) => { const d = new Date(t * 1000); return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`; };

function RunPodKey({ k, onSave, onClear, busy }: { k: StatusReply['runpodKey']; onSave: (key: string) => Promise<boolean>; onClear: () => void; busy: boolean }) {
  const [v, setV] = useState('');
  const has = !!k;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', padding: 14 }}>
      <label className="field" style={{ margin: 0, flex: '1 1 260px', maxWidth: 420 }}>
        <span className="field__label">{has ? 'Replace the key' : 'RunPod API key'}</span>
        <input className="field__input mono" type="password" autoComplete="off" spellCheck={false} placeholder={has ? `…${k.last4}` : 'rpa_…'} value={v} onChange={(e) => setV(e.target.value)} />
      </label>
      <button type="button" className="btn btn--primary" disabled={busy || v.trim().length < 16} onClick={() => void onSave(v.trim()).then((ok) => ok && setV(''))}>{busy ? 'Checking…' : 'Save'}</button>
      {k?.source === 'saved' && <button type="button" className="btn" disabled={busy} onClick={onClear}>Remove</button>}
      <span style={{ fontSize: 12, color: 'var(--ink-55)', paddingBottom: 8 }}>
        {!k ? 'No key: the pool cannot start pods and the balance is unknown.'
          : k.source === 'saved' ? `Key …${k.last4}, saved here${k.setAt ? ` ${fmtDate(k.setAt)}` : ''}. The API's pool loop and this page use it.`
          : `Key …${k.last4} from the RUNPOD_API_KEY secret. A key saved here takes over.`}
        {' '}RunPod checks a key before it is saved; it is stored encrypted and never shown again.
      </span>
    </div>
  );
}

function VastFields({ draft, set }: { draft: PoolSettings; set: (s: PoolSettings) => void }) {
  const v: VastSettings = draft.vast ?? { enabled: true, image: '', diskGb: 40, minGpuRamGb: 16, minReliability: 0.97, minCuda: 12.4, minInetDownMbps: 200, minCpuCores: 8, minRamGb: 32, bootGb: 12, excludeCountries: ['CN'] };
  const sm = draft.balancer.speechMachines ?? { enabled: false, maxPerH: 0.6, readyTimeoutS: 900, heartbeatTimeoutS: 45, retryS: 120, racers: 3, bootCapS: 150 };
  const setSm = (x: Partial<typeof sm>) => set({ ...draft, balancer: { ...draft.balancer, speechMachines: { ...sm, ...x } } });
  const setV = (x: Partial<VastSettings>) => set({ ...draft, vast: { ...v, ...x } });
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div className="tbl__h">VAST: VOICES WHEN RUNPOD HAS NO GPU</div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <Toggle on={v.enabled} onChange={(on) => setV({ enabled: on })} label="Race speech-only Vast machines at RunPod's first refusal" />
        <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>Voices only (readings, clones, previews, background sound, live voices); never a transcription. Needs VAST_API_KEY and PUBLIC_API_ORIGIN on the API.</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
        <Field label="At most ($/h, all in)" type="number" hint="0.60 at most" value={String(sm.maxPerH)} onChange={(x) => set({ ...draft, balancer: { ...draft.balancer, speechMachines: { ...sm, maxPerH: Math.max(0.01, Math.min(0.6, Number(x) || 0.6)) } } })} />
        <Field label="Racers" type="number" hint="Vast machines rented at once; the first ready speaks, the rest are destroyed" value={String(sm.racers ?? 3)} onChange={(x) => setSm({ racers: Math.max(1, Math.min(5, Math.round(Number(x) || 3))) })} />
        <Field label="Boot cap (s)" type="number" hint="a racer not ready by then is destroyed and the next offer races; doubles while every host is slow" value={String(sm.bootCapS ?? 150)} onChange={(x) => setSm({ bootCapS: Math.max(30, Math.min(sm.readyTimeoutS, Math.round(Number(x) || 150))) })} />
        <Field label="Boot weight" type="number" hint="price penalty per minute of estimated pull (network and disk); 0 ranks by price alone" value={String(v.bootWeight ?? 0.05)} onChange={(x) => setV({ bootWeight: Math.max(0, Math.min(1, Number(x) || 0)) })} />
        <Field label="Image" mono hint="the speech machine's container image" value={v.image} onChange={(x) => setV({ image: x.trim() })} />
        <Field label="GPU memory (GB, at least)" type="number" value={String(v.minGpuRamGb)} onChange={(x) => setV({ minGpuRamGb: Math.max(12, Math.round(Number(x) || 16)) })} />
        <Field label="Host reliability (at least)" type="number" value={String(v.minReliability)} onChange={(x) => setV({ minReliability: Math.max(0, Math.min(1, Number(x) || 0)) })} />
        <Field label="Download (Mbps, at least)" type="number" value={String(v.minInetDownMbps)} onChange={(x) => setV({ minInetDownMbps: Math.max(0, Number(x) || 0) })} />
        <Field label="Leave out countries" mono hint="two-letter codes, comma separated" value={v.excludeCountries.join(', ')} onChange={(x) => setV({ excludeCountries: x.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean) })} />
      </div>
    </div>
  );
}

type RaceOutcome = { t: string; tone: Tone };
export function raceOutcome(p: Pick<PodRow, 'readyAt' | 'endedAt' | 'endReason' | 'createdAt'>, now = Date.now()): RaceOutcome {
  const r = (p.endReason ?? '').toLowerCase();
  const secs = (iso: string) => Math.round((Date.parse(iso) - Date.parse(p.createdAt)) / 1000);
  if (p.readyAt) return { t: `won: ready in ${secs(p.readyAt)} s`, tone: 'ok' };
  if (!p.endedAt) return { t: `racing ${Math.max(0, Math.round((now - Date.parse(p.createdAt)) / 1000))} s`, tone: 'info' };
  if (r.includes('lost the race') || r.includes('ready in the same moment')) return { t: `lost after ${secs(p.endedAt)} s`, tone: 'mute' };
  if (r.includes('a slow host') || r.includes('not ready after')) return { t: `too slow: ended at ${secs(p.endedAt)} s`, tone: 'warn' };
  return { t: `ended at ${secs(p.endedAt)} s`, tone: 'bad' };
}
export function latestRace<T extends Pick<PodRow, 'speechOnly' | 'createdAt' | 'id'>>(pods: T[], windowMs = 15 * 60_000): T[] {
  const sp = pods.filter((p) => p.speechOnly).sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt) || b.id - a.id);
  if (!sp.length) return [];
  const newest = Date.parse(sp[0].createdAt);
  return sp.filter((p) => newest - Date.parse(p.createdAt) <= windowMs);
}

const money = (v: number) => `$${v.toFixed(v < 10 ? 2 : 0)}`;
const lasts = (h: number) => (h >= 48 ? `${(h / 24).toFixed(1)} d` : `${h.toFixed(1)} h`);
const ago = (iso: string | null) => (iso ? `${Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000))} s ago` : '—');

export function GpuPool() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const st = useData<StatusReply>(env, '/admin/gpu-pool', 5, paused);
  const [range, setRange] = useState<Range>('24');
  const hist = useData<{ samples: Sample[] }>(env, `/admin/gpu-pool/samples?hours=${range}`, 60, paused, [range]);
  const [logKinds, setLogKinds] = useState({ pods: true, moves: true, places: false });
  const [keyBusy, setKeyBusy] = useState(false);
  const [allPods, setAllPods] = useState(false);
  const [draft, setDraft] = useState<PoolSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const saved = st.data?.settings;
  useEffect(() => { if (saved && !draft) setDraft(saved); }, [saved, draft]);
  const dirty = !!draft && !!saved && JSON.stringify(draft) !== JSON.stringify(saved);
  const err = describeErr(st.error, 'API');

  const save = async (next: PoolSettings) => {
    setBusy(true);
    try {
      await put(env, '/admin/settings/gpu_pool', next);
      setDraft(null);
      st.reload();
      ctx.toast('GPU pool settings saved', `Mode: ${next.mode}`);
    } catch (e) { ctx.toast('Not saved', (e as ApiErr).message, { logged: false }); }
    finally { setBusy(false); }
  };

  const saveKey = async (key: string): Promise<boolean> => {
    setKeyBusy(true);
    try {
      const r = await put<{ balance: number }>(env, '/admin/gpu-pool/runpod-key', { key });
      st.reload();
      ctx.toast('RunPod key saved', `Balance $${r.balance.toFixed(2)}`);
      return true;
    } catch (e) { ctx.toast('Key not saved', (e as ApiErr).message, { logged: false }); return false; }
    finally { setKeyBusy(false); }
  };
  const clearKey = async () => {
    setKeyBusy(true);
    try { await del(env, '/admin/gpu-pool/runpod-key'); st.reload(); ctx.toast('RunPod key removed', 'The RUNPOD_API_KEY secret is used if set.'); }
    catch (e) { ctx.toast('Not removed', (e as ApiErr).message, { logged: false }); }
    finally { setKeyBusy(false); }
  };

  const series = hist.data?.samples ?? [];
  const xs = series.map((x) => x.t);
  const fmtX = useCallback((t: number) => fmtClock(t), []);
  const decisions = useMemo(() => (st.data?.decisions ?? []).filter((x) => { const k = kindOf(x); return k === 'pods' ? logKinds.pods : k === 'moves' ? logKinds.moves : logKinds.places; }), [st.data?.decisions, logKinds]);

  const d = st.data;
  const open = (d?.pods ?? []).filter((p) => !p.endedAt && !p.watch);
  const watchPods = Object.values(d?.balancer?.pods ?? {}).filter((p) => p.state !== 'gone').length;
  const sessions = Object.values(d?.balancer?.sessions ?? {});
  const onPods = sessions.filter((s) => s.place.at === 'pod').length;
  const demand = d?.balancer?.demand;
  const cap = d?.settings.balancer.spendCapPerDay ?? 0;
  const mode = d?.settings.mode ?? 'off';
  const acct = d?.runpodAccount ?? null;
  const shim = draft ? ({ balancer: draft.balancer } as SimConfig) : null;
  const setShim = (c: SimConfig) => draft && setDraft({ ...draft, balancer: c.balancer });

  const watchRows: PodRow[] = mode === 'watch' ? Object.values(d?.balancer?.pods ?? {}).filter((p) => p.state !== 'gone').map((p) => ({
    id: p.id, providerId: null, gpu: p.gpu.name, slots: p.gpu.slots, perH: p.gpu.perH, costPerH: null, state: p.state, watch: true,
    slotsUp: p.state === 'ready' || p.state === 'draining' ? p.gpu.slots : 0,
    slotsTaken: sessions.filter((s) => s.place.at === 'pod' && s.place.pod === p.id).length,
    createdAt: new Date(p.createdAt * 1000).toISOString(), containerAt: null, readyAt: null, heartbeatAt: null, endedAt: null, endReason: null,
  })) : [];
  const started = new Map<number, string>();
  const ended = new Map<number, string>();
  for (const x of [...(d?.decisions ?? [])].reverse()) {
    const a = x.action as { do?: string; pod?: number };
    if (a.pod == null) continue;
    if (a.do === 'createPod' || a.do === 'resumePod' || a.do === 'startSpeechMachine') started.set(a.pod, x.why);
    if (a.do === 'terminatePod' || a.do === 'stopPod') ended.set(a.pod, x.why);
  }
  const podWhy = (id: number) => plainWhy(ended.get(id)) ?? plainWhy(started.get(id));
  const podSince = (id: number) => {
    const p = (d?.pods ?? []).find((x) => x.id === id);
    if (!p?.createdAt) return null;
    const at = new Date(p.createdAt);
    const hm = (t: Date) => t.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    const span = (ms: number) => { const m = Math.max(0, Math.round(ms / 60_000)); return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`; };
    const ready = p.readyAt ? ` · ready ${hm(new Date(p.readyAt))}` : '';
    if (p.endedAt) {
      const end = new Date(p.endedAt);
      return `started ${hm(at)}${ready} · ended ${hm(end)} · up ${span(end.getTime() - at.getTime())}`;
    }
    return `started ${hm(at)}${ready} · up ${span(Date.now() - at.getTime())} so far`;
  };
  const podRows: Row[] = [...watchRows, ...(d?.pods ?? []).filter((p) => !p.watch)].map((p) => {
    const refused = !p.providerId && !!p.endedAt && noStock(p.endReason);
    return {
    key: String(p.id),
    cells: [
      { t: `pod ${p.id}${p.speechOnly ? ' · speech only' : ''}`, sub: `${p.watch ? '' : `${providerName(p)} `}${p.providerId ?? (p.watch ? 'watch: not created' : refused ? 'never created' : 'not created yet')}`, mono: true },
      { pill: refused ? { label: 'no stock', tone: 'warn' as Tone } : { label: p.endedAt ? 'gone' : p.state, tone: p.endedAt ? 'mute' : STATE_TONE[p.state] ?? 'mute' } },
      { t: p.gpuName ?? p.gpuTypeId ?? p.gpu, sub: `${p.gpuName && p.gpuName !== p.gpu ? `${p.gpu} · ` : ''}${money(p.costPerH ?? p.perH)}/h${p.dataCenter ? ` · ${p.dataCenter}` : ''}${p.rttMs != null ? ` · ${Math.round(p.rttMs)} ms` : ''}` },
      p.speechOnly ? { t: 'none (voices only)', sub: p.tts ? `speech ${speechOf(p.tts).busy} of ${speechOf(p.tts).lanes}` : undefined } : { t: `${p.slotsTaken} of ${p.slots}`, sub: `${p.slotsUp} up${p.sessions ? ` · ${p.sessions} served` : ''}${p.tts && !p.watch ? ` · speech ${speechOf(p.tts).busy} of ${speechOf(p.tts).lanes}` : ''}` },
      p.watch ? { t: '—' } : ttsCell(p.tts),
      { t: fmtDate(p.createdAt), sub: p.readyAt ? `ready ${fmtDate(p.readyAt)}` : p.containerAt ? `container ${fmtDate(p.containerAt)}` : undefined },
      { t: p.endedAt ? fmtDate(p.endedAt) : ago(p.heartbeatAt), sub: refused ? stockNote(p.asked?.dataCenters) : p.endReason ?? undefined },
      { t: plainWhy(started.get(p.id)) ?? '—', sub: p.endedAt ? plainWhy(ended.get(p.id)) ?? plainWhy(p.endReason) ?? undefined : undefined },
    ],
    };
  });
  const race = latestRace(d?.pods ?? []);
  const raceEvents = (id: number, kind: string) => (d?.launches ?? []).find((e) => e.pod === id && e.kind === kind)?.detail as Record<string, any> | undefined;
  const raceRows: Row[] = race.map((p) => {
    const o = raceOutcome(p);
    const made = raceEvents(p.id, 'created');
    const boot = raceEvents(p.id, 'boot');
    return {
      key: String(p.id),
      cells: [
        { t: `pod ${p.id}`, sub: `${providerName(p)} ${p.providerId ?? 'not created'}`, mono: true },
        { t: o.t, fg: o.tone === 'ok' ? 'var(--ok)' : o.tone === 'warn' ? 'var(--conf-mid)' : o.tone === 'bad' ? 'var(--conf-low)' : undefined },
        { t: p.gpuName ?? p.gpuTypeId ?? '—', sub: [p.dataCenter, p.costPerH != null ? `$${p.costPerH.toFixed(2)}/h` : null].filter(Boolean).join(' · ') || undefined },
        { t: made?.estBootS != null ? `~${made.estBootS} s` : '—', sub: made?.inetDown ? `${Math.round(made.inetDown)} Mbps${made.diskBw ? ` · disk ${Math.round(made.diskBw)} MB/s` : ''}` : undefined },
        { t: boot ? [boot.startedAfterS != null ? `image ${boot.startedAfterS} s` : null, boot.bundle_s != null ? `bundle ${boot.bundle_s} s` : null, boot.tts_s != null ? `voices ${boot.tts_s} s` : null].filter(Boolean).join(' · ') || '—' : '—' },
        { t: plainWhy(p.endReason) ?? '—' },
      ],
    };
  });
  const launches = d?.launches ?? [];
  const failsToday = launches.filter((e) => (e.kind === 'ended' || (e.kind === 'create_failed' && !(e.detail as { noStock?: boolean }).noStock && !noStock(String((e.detail as { error?: string }).error ?? '')))) && Date.now() - Date.parse(e.at) < 86_400_000).length;
  const launchRows: Row[] = launches.map((e, i) => {
    const c = launchCells(e);
    return {
      key: `${e.at}-${i}`,
      cells: [
        { t: hhmmss(Date.parse(e.at)), sub: fmtDate(e.at), mono: true },
        { t: e.pod == null ? '—' : `pod ${e.pod}`, mono: true },
        { t: c.what, fg: c.fg },
        { t: c.detail, sub: c.sub, fg: c.fg },
      ],
    };
  });
  const fleet = d?.fleet ?? null;
  const fs = fleet?.sessions ?? [];
  const fOnFly = fs.filter((x) => x.place.at === 'fly').length;
  const fOnGpu = fs.filter((x) => x.place.at === 'pod').length;
  const fWaiting = fs.filter((x) => x.place.at === 'waiting').length;
  const fUp = (fleet?.pods ?? []).filter((p) => p.state === 'ready' || p.state === 'draining').length;
  const fComing = (fleet?.pods ?? []).filter((p) => p.state === 'creating' || p.state === 'booting').length;
  const a24 = d?.actions24h ?? {};
  const last = series[series.length - 1];
  const minPods = d?.settings.balancer.minPods ?? 0;

  return (
    <>
      <Head title="GPU pool" sub="RunPod pods beside the Fly engines, started and stopped by the balancer the simulator runs. One API machine runs it every 5 s."
        acts={<button type="button" className="btn btn--sm" onClick={() => ctx.go('simulator')}>Simulator</button>} />
      <Grid>
        <Cell span={12}>
          <Block loading={st.loading && !d} error={!d && err ? { ...err, retry: st.reload } : null}>
            <Strip items={[
              { k: 'MODE', v: mode.toUpperCase(), fg: mode === 'live' ? 'var(--ok)' : mode === 'watch' ? 'var(--brand)' : undefined },
              { k: 'PODS', v: mode === 'watch' ? `${watchPods} (watch)` : String(open.length) },
              { k: 'ON PODS', v: `${onPods} of ${sessions.length} sessions` },
              { k: 'SPEND TODAY', v: `${money(d?.spendToday ?? 0)}${cap ? ` of ${money(cap)}` : ''}${mode === 'watch' ? ' (would be)' : ''}` },
              { k: 'LAST RUN', v: ago(d?.savedAt ?? null) },
              acct && 'balance' in acct
                ? { k: 'RUNPOD BALANCE', v: `$${acct.balance.toFixed(2)}${acct.low ? ' (low)' : ''}`, fg: acct.low ? 'var(--conf-low)' : 'var(--ok)' }
                : { k: 'RUNPOD', v: acct ? 'no answer' : d?.runpodConfigured ? 'key set' : 'no key', fg: (d && !d.runpodConfigured && mode === 'live') || acct ? 'var(--conf-low)' : undefined },
              { k: 'VAST (VOICES)', v: d?.speechMachines ? 'fallback on' : d?.vastConfigured ? 'off' : 'no key', fg: d?.speechMachines ? 'var(--ok)' : undefined },
              ...(acct && 'balance' in acct ? [{ k: 'LASTS', v: acct.hoursLeft == null ? 'nothing running' : `${lasts(acct.hoursLeft)} at ${money(acct.spendPerH)}/h`, fg: acct.low ? 'var(--conf-low)' : undefined }] : []),
              { k: 'LAUNCH FAILURES 24H', v: String(failsToday), fg: failsToday ? 'var(--conf-low)' : undefined },
            ]} />
          </Block>
        </Cell>
        {demand?.speechCapped && (
          <Cell span={12}><Block bg="var(--conf-mid-soft)"><Banner icon="warn" fg="var(--conf-mid)" text="The emergency brake on voices is on: voices with no session start and keep no pod, and listeners are told voices are busy. Clear it under Settings." sub={demand.speechCapped} /></Block></Cell>
        )}
        {d?.balancer && (demand?.blocked || (fOnFly > 0 && !open.some((p) => !p.speechOnly))) && (
          <Cell span={12}><WhyNoGpu bal={d.balancer as Snap} blocked={demand?.blocked ?? null} onFly={fOnFly} mobile={mobile} /></Cell>
        )}
        {(d?.alerts ?? []).map((a) => (
          <Cell key={a} span={12}><Block bg="var(--conf-mid-soft)"><Banner icon="warn" fg="var(--conf-low)" text={a} /></Block></Cell>
        ))}
        <Cell span={12}>
          <Block title="RunPod" sub="The account key the pool starts pods with and this page reads the balance with.">
            {d && <RunPodKey k={d.runpodKey} busy={keyBusy} onSave={saveKey} onClear={() => confirmSimple(ctx, { title: 'Remove the RunPod key', app: 'GPU pool', danger: true, body: 'The key saved here is deleted; the RUNPOD_API_KEY secret is used if set.', confirmLabel: 'Remove key', onConfirm: clearKey })} />}
          </Block>
        </Cell>
        {d && (
          <Cell span={12}>
            <Kpis min={150} items={[
              { label: 'Sessions', v: `${fs.length}`, sub: `${fOnFly} on Fly · ${fOnGpu} on GPU${fWaiting ? ` · ${fWaiting} waiting` : ''}` },
              { label: 'Pods', v: `${fUp}${fComing ? ` + ${fComing}` : ''}`, sub: `${fComing ? `${fComing} coming up · ` : ''}${minPods ? `${minPods} always on` : 'none always on'}` },
              demand
                ? { label: 'Demand', v: demand.overflow.toFixed(1), sub: demand.blocked ? `no new pod: ${demand.blocked}` : demand.pressure ? `${demand.pressure}: pods start at once` : demand.startIn != null ? `over ${demand.startAt.toFixed(1)}: a pod in ${Math.ceil(demand.startIn)} s` : `starts at ≥ ${demand.startAt.toFixed(1)} · ${demand.spare} spare slots`, fg: demand.blocked && demand.overflow > 0 ? 'var(--conf-low)' : demand.pressure ? 'var(--conf-mid)' : undefined }
                : { label: 'Demand', v: '–', sub: 'the balancer has not run' },
              { label: 'Moves 24 h', v: `${a24.move ?? 0}`, sub: `${(a24.createPod ?? 0) + (a24.resumePod ?? 0)} pods started · ${a24.drain ?? 0} drained` },
              { label: 'Turned away 24 h', v: `${a24.reject ?? 0}`, fg: a24.reject ? 'var(--conf-low)' : undefined, sub: 'no Fly machine and no GPU slot' },
              { label: 'Cost now', v: last ? `$${last.costRate.toFixed(2)}/h` : '–', sub: last ? `all on Fly: $${last.baselineRate.toFixed(2)}/h` : 'no sample yet' },
            ]} />
          </Cell>
        )}
        <Cell span={12}>
          <Block title="Fleet" sub={mode === 'watch' ? 'Live sessions, and the pods the balancer would have (watch mode: none was created). Each dot is one transcription.' : 'Each dot is one transcription, on a Fly machine or in a pod\'s slot; a moving dot is a handoff.'} foot={<FlowLegend />}
            empty={d && !fleet ? 'The balancer has not run yet (mode off, or no API machine holds its lock).' : undefined}>
            {fleet && <div style={{ padding: 14 }}><Flow snap={fleet} mobile={mobile} why={podWhy} since={podSince} /></div>}
          </Block>
        </Cell>
        <Cell span={12}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Seg<Range> value={range} onChange={setRange} mono options={RANGES.map((x) => ({ value: x.value, label: x.label }))} /></div>
        </Cell>
        <Cell span={mobile ? 12 : 4}>
          <Block title="Demand" sub="Weighted sessions with no GPU slot (Multilingual 1, English 0.5), against the start and drain lines.">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => v.toFixed(1)} empty="No samples yet: one a minute while the balancer runs." lines={[
                { label: 'Demand', vals: series.map((x) => x.overflow), color: 'var(--brand)' },
                { label: 'Start line', vals: series.map((x) => (Number.isFinite(x.startAt) ? x.startAt : NaN)), color: 'var(--ink-62)', dash: true },
                { label: 'Drain line', vals: series.map((x) => (Number.isFinite(x.drainAt) ? x.drainAt : NaN)), color: 'var(--ink-40)', dash: true },
              ]} />
            </div>
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 4}>
          <Block title="Where sessions run" sub="Sessions on Fly machines and in GPU slots, and files waiting, each minute.">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => String(Math.round(v))} empty="No samples yet." lines={[
                { label: 'On GPU', vals: series.map((x) => x.onGpu), color: 'var(--brand)' },
                { label: 'On Fly', vals: series.map((x) => x.onFly), color: 'var(--ink-55)' },
                { label: 'Waiting', vals: series.map((x) => x.waiting), color: 'var(--conf-mid)', dash: true },
              ]} />
            </div>
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 4}>
          <Block title="Cost per hour" sub="What the fleet costs, against everything on Fly.">
            <div style={{ padding: '8px 10px 10px' }}>
              <LineChart xs={xs} fmtX={fmtX} fmtY={(v) => `$${v.toFixed(2)}`} empty="No samples yet." lines={[
                { label: 'With GPU', vals: series.map((x) => x.costRate), color: 'var(--brand)' },
                { label: 'All on Fly', vals: series.map((x) => x.baselineRate), color: 'var(--ink-55)', dash: true },
              ]} />
            </div>
          </Block>
        </Cell>
        {open.length > 0 && (
          <Cell span={12}>
            <Block title="Capacity" sub="Each open pod's transcription slots and its TTS server's share of the GPU. A pod full on either takes no new session." asOf={hhmmss(st.at)}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 12, padding: 14 }}>
                {open.map((p) => <PodCapacity key={p.id} p={p} />)}
              </div>
            </Block>
          </Cell>
        )}
        <Cell span={12}>
          <Block title="Pods" count={mode === 'watch' ? `${watchRows.length} (watch)` : `${open.length} open`} asOf={hhmmss(st.at)} empty={d && !podRows.length ? 'No pod in the last day.' : undefined}>
            <Table cols={[{ label: 'Pod', width: 'minmax(140px, 1fr)' }, { label: 'State', width: '110px' }, { label: 'GPU', width: '170px' }, { label: 'Slots taken', width: '120px' }, { label: 'TTS', width: '150px' }, { label: 'Created', width: '170px' }, { label: 'Heartbeat / ended', width: 'minmax(160px, 1fr)' }, { label: 'Why started / why ended', width: 'minmax(240px, 1.4fr)' }]} rows={allPods ? podRows : podRows.slice(0, POD_ROWS)} minWidth={1260} mobile={mobile} />
            {podRows.length > POD_ROWS && (
              <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'center' }}>
                <button type="button" className="btn btn--sm" onClick={() => setAllPods(!allPods)}>{allPods ? `Show the newest ${POD_ROWS}` : `Show all ${podRows.length} pods`}</button>
              </div>
            )}
          </Block>
        </Cell>
        {raceRows.length > 0 && (
          <Cell span={12}>
            <Block title="Speech race" count={`${raceRows.length} machines`} sub="The latest race for a speech machine: Vast offers rented at once beside the RunPod ask; the first with its voice server answering speaks and the rest are destroyed." asOf={hhmmss(st.at)}>
              <Table cols={[{ label: 'Machine', width: 'minmax(140px, 1fr)' }, { label: 'Outcome', width: '170px' }, { label: 'GPU', width: '200px' }, { label: 'Expected boot', width: '160px' }, { label: 'Boot', width: '220px' }, { label: 'Why ended', width: 'minmax(220px, 1.4fr)' }]} rows={raceRows} minWidth={1100} mobile={mobile} />
            </Block>
          </Cell>
        )}
        <Cell span={12}>
          <Block title="Launch log" sub="What RunPod said as each pod launched, what it refused, and why a launch failed (the last 300)." asOf={hhmmss(st.at)} empty={d && !launchRows.length ? 'No launch yet.' : undefined}>
            <div className="tbl--wrap">
              <Table cols={[{ label: 'At', width: '110px' }, { label: 'Pod', width: '80px' }, { label: 'What', width: '120px' }, { label: 'Detail', width: 'minmax(320px, 1fr)' }]} rows={launchRows} minWidth={760} mobile={mobile} />
            </div>
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="Settings" sub={draft ? MODE_SUB[draft.mode] : undefined}>
            {draft && shim && (
              <div style={{ display: 'grid', gap: 16, padding: 14 }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Seg value={draft.mode} options={MODES} onChange={(m) => setDraft({ ...draft, mode: m })} />
                  <Seg value={draft.balancer.strategy ?? 'demand'} options={STRATEGIES} onChange={(v) => setDraft({ ...draft, balancer: { ...draft.balancer, strategy: v } })} />
                  <Toggle on={draft.balancer.startOnNew} onChange={(on) => setDraft({ ...draft, balancer: { ...draft.balancer, startOnNew: on } })} label="New sessions start pods" />
                  <button type="button" className="btn btn--sm" onClick={() => ctx.go('gpu-types')}>GPU types and tests</button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-55)' }}>{STRATEGY_SUB[draft.balancer.strategy ?? 'demand']}</div>
                <GpuGroups b={draft.balancer} set={(b) => setDraft({ ...draft, balancer: b })} />
                <Fields title="GPU POOL" items={POOL} cfg={shim} set={setShim} />
                <Fields title="RULES" items={RULES} cfg={shim} set={setShim} />
                <Fields title="VOICES WITH NO SESSION" items={SPEECH} cfg={shim} set={setShim} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                  <Field label="Voice asks a minute, each account" type="number" hint="that start or keep a pod; past it a 429. Share links never start a pod"
                    value={String(draft.speech?.accountAsksPerMin ?? 120)}
                    onChange={(v) => setDraft({ ...draft, speech: { ...draft.speech, accountAsksPerMin: Math.max(0, Math.round(Number(v) || 0)) } })} />
                </div>
                <SpeechBrake cfg={shim} set={setShim} />
                <VastFields draft={draft} set={setDraft} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                  <Field label="Image (stock CUDA)" mono value={draft.runpod.image} onChange={(v) => setDraft({ ...draft, runpod: { ...draft.runpod, image: v } })} />
                  <Field label="GPU tests a day ($)" type="number" hint="admin and CLI cold tests together" value={String(draft.runpod.testSpendCapPerDay ?? 5)} onChange={(v) => setDraft({ ...draft, runpod: { ...draft.runpod, testSpendCapPerDay: Math.max(0, Number(v) || 0) } })} />
                  <Field label="Container disk (GB)" type="number" value={String(draft.runpod.containerDiskGb)} onChange={(v) => setDraft({ ...draft, runpod: { ...draft.runpod, containerDiskGb: Math.max(10, Math.round(Number(v) || 0)) } })} />
                  <Field label="Low balance alert ($)" type="number" hint="also under a day's spend left" value={String(draft.runpod.lowBalanceUsd ?? 50)} onChange={(v) => setDraft({ ...draft, runpod: { ...draft.runpod, lowBalanceUsd: Math.max(0, Number(v) || 0) } })} />
                  <Field label="vCPU per GPU (at least)" type="number" value={String(draft.runpod.minVcpuPerGpu)} onChange={(v) => setDraft({ ...draft, runpod: { ...draft.runpod, minVcpuPerGpu: Math.max(1, Math.round(Number(v) || 0)) } })} />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" className="btn btn--primary" disabled={!dirty || busy} onClick={() => void save(draft)}>{busy ? 'Saving…' : 'Save'}</button>
                  <button type="button" className="btn" disabled={!dirty || busy} onClick={() => setDraft(saved ?? null)}>Undo</button>
                  <span style={{ fontSize: 12, color: 'var(--ink-55)' }}>Secure Cloud only. A change reaches the balancer at its next step; running pods keep their GPU type.</span>
                </div>
              </div>
            )}
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="Decisions" count={decisions.length} sub="Newest first, each with the balancer's reason and what came of it (the last 200; kept 30 days)."
            chips={[
              { label: 'Pods', on: logKinds.pods, onClick: () => setLogKinds({ ...logKinds, pods: !logKinds.pods }) },
              { label: 'Moves', on: logKinds.moves, onClick: () => setLogKinds({ ...logKinds, moves: !logKinds.moves }) },
              { label: 'Placements', on: logKinds.places, onClick: () => setLogKinds({ ...logKinds, places: !logKinds.places }) },
            ]}
            empty={d && !decisions.length ? 'No decisions of these kinds yet.' : undefined}>
            {decisions.length > 0 && <div style={{ maxHeight: 420, overflow: 'auto' }}><Log lines={decisions.map((x) => {
              const stockOut = x.action.do === 'createPod' && noStock(x.outcome?.replace(/^failed: /, ''));
              const stockEnd = x.action.do === 'terminatePod' && /RunPod has no .* free/.test(x.why);
              const ts = `${fmtDate(x.at)}${x.mode !== 'live' ? ` · ${x.mode}` : ''}`;
              if (stockOut) {
                const dcs = 'dataCenters' in x.action ? (x.action as { dataCenters?: string[] }).dataCenters : undefined;
                return { ts, t: `${x.why} → ${stockNote(dcs)}`, tone: 'warn' as const, hl: false };
              }
              if (stockEnd) return { ts, t: x.why.replace(/^pod (\d+): RunPod has no/, 'pod $1 not created: RunPod has no'), tone: 'mute' as const, hl: false };
              return {
                ts,
                t: x.outcome && x.outcome !== 'watch' ? `${x.why} → ${x.outcome}` : x.why,
                tone: x.outcome?.startsWith('failed') || x.outcome?.startsWith('reaped') ? 'bad' : toneOf(x),
                hl: x.action.do === 'createPod' || x.action.do === 'resumePod' || x.action.do === 'startSpeechMachine',
              };
            })} /></div>}
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
